export class EventBus{
  constructor(){this.map=new Map()}
  on(e,f){if(!this.map.has(e))this.map.set(e,new Set());this.map.get(e).add(f)}
  emit(e,p={}){for(const f of this.map.get(e)??[])f(p)}
}

export class ExperienceRuntime{
  constructor({schema,adapter,events,onState,onDecision}){
    this.schema=schema;
    this.adapter=adapter;
    this.events=events;
    this.onState=onState;
    this.onDecision=onDecision ?? (()=>{});
    this.state={
      currentStepIndex:0,
      completed:false,
      variables:structuredClone(schema.variables??{}),
      feedback:null
    };
    this.timers=new Map();
    this.stepMeta=new Map();
  }

  get currentStep(){return this.schema.steps[this.state.currentStepIndex]??null}

  async start(){
    await this.adapter.loadScene(this.schema.scene);
    const supported=[
      'user.action','player.enter_zone','player.exit_zone','vehicle.accelerate','vehicle.brake',
      'button.pressed','object.grabbed','object.gazed','collision.enter','timer.completed'
    ];
    for(const e of supported)this.events.on(e,p=>this.handle(e,p));
    this.notify('experience.started');
  }

  meta(stepId){
    if(!this.stepMeta.has(stepId)){
      this.stepMeta.set(stepId,{
        lastEventAt:0,
        failures:0,
        consumed:false
      });
    }
    return this.stepMeta.get(stepId);
  }

  async handle(event,payload={}){
    if(this.state.completed)return;

    const step=this.currentStep;
    if(!step || step.trigger?.event!==event)return;
    if(step.trigger.target && payload.target!==step.trigger.target)return;

    const policy=step.policy ?? {
      cooldownMs:500,
      maxFailures:3,
      consumeOnSuccess:true,
      consumeOnFailure:false
    };

    const meta=this.meta(step.id);
    const now=Date.now();

    if(meta.consumed){
      this.logDecision(step,event,'ignored','step_consumed',payload);
      return;
    }

    if(now-meta.lastEventAt < (policy.cooldownMs??0)){
      this.logDecision(step,event,'ignored','cooldown',payload);
      return;
    }
    meta.lastEventAt=now;

    const conditionResults=(step.conditions??[]).map(c=>({
      condition:c,
      passed:this.check(c,payload)
    }));
    const success=conditionResults.every(r=>r.passed);

    if(!success && policy.maxFailures!=null && meta.failures>=policy.maxFailures){
      this.logDecision(step,event,'ignored','max_failures_reached',payload,conditionResults);
      return;
    }

    if(success){
      for(const a of step.successActions??[])await this.exec(a,payload);
      if(policy.consumeOnSuccess)meta.consumed=true;
      this.logDecision(step,event,'success','conditions_passed',payload,conditionResults);
      this.notify('step.success');
      return;
    }

    meta.failures += 1;
    for(const a of step.failActions??[])await this.exec(a,payload);
    if(policy.consumeOnFailure)meta.consumed=true;

    this.logDecision(
      step,event,'failure',
      policy.maxFailures!=null
        ? `failure_${meta.failures}_of_${policy.maxFailures}`
        : `failure_${meta.failures}`,
      payload,conditionResults
    );
    this.notify('step.fail');
  }

  check(c){
    const vars=this.state.variables;
    if(c.type==='object.exists')return this.adapter.hasObject(c.objectId);
    if(c.type==='variable.equals')return vars[c.key]===c.value;
    if(c.type==='variable.less_than')return Number(vars[c.key])<Number(c.value);
    if(c.type==='variable.greater_than')return Number(vars[c.key])>Number(c.value);
    if(c.type==='object.state_equals')return this.adapter.getObjectState(c.objectId,c.key)===c.value;
    if(c.type==='player.in_zone')return this.adapter.isPlayerInZone(c.zoneId);
    return false;
  }

  async exec(a){
    const vars=this.state.variables;
    switch(a.type){
      case 'score.add':
        vars.score=(Number(vars.score)||0)+(Number(a.value)||0);
        break;
      case 'score.subtract':
        vars.score=(Number(vars.score)||0)-(Number(a.value)||0);
        break;
      case 'variable.set':
        vars[a.key]=a.value;
        break;
      case 'variable.add':
        vars[a.key]=(Number(vars[a.key])||0)+(Number(a.value)||0);
        break;
      case 'object.show':
        this.adapter.setObjectVisible(a.objectId,true);
        break;
      case 'object.hide':
        this.adapter.setObjectVisible(a.objectId,false);
        break;
      case 'object.state.set':
        this.adapter.setObjectState(a.objectId,a.key,a.value);
        break;
      case 'feedback.show':
        this.state.feedback=String(a.value??'');
        break;
      case 'timer.start':
        this.startTimer(a.timerId,a.durationMs);
        break;
      case 'step.next':
        this.state.feedback=null;
        this.state.currentStepIndex=Math.min(
          this.state.currentStepIndex+1,
          this.schema.steps.length-1
        );
        break;
      case 'experience.complete':
        this.state.completed=true;
        break;
    }
  }

  startTimer(id,durationMs){
    if(!id)return;
    if(this.timers.has(id))clearTimeout(this.timers.get(id));
    const h=setTimeout(()=>{
      this.timers.delete(id);
      this.events.emit('timer.completed',{target:id})
    },durationMs??1000);
    this.timers.set(id,h);
  }

  setVariable(key,value){
    this.state.variables[key]=value;
    this.notify('variable.changed');
  }

  logDecision(step,event,outcome,reason,payload={},conditions=[]){
    this.onDecision({
      ts:new Date().toISOString(),
      stepId:step.id,
      event,
      target:payload.target ?? null,
      outcome,
      reason,
      score:this.state.variables.score ?? 0,
      conditions:conditions.map(r=>({
        type:r.condition.type,
        key:r.condition.key ?? null,
        objectId:r.condition.objectId ?? null,
        expected:r.condition.value ?? null,
        passed:r.passed
      }))
    });
  }

  notify(type){
    this.onState({
      type,
      step:this.currentStep,
      state:structuredClone(this.state)
    });
  }
}
